import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { PageShell } from "@/components/PageShell";
import { Breadcrumbs, breadcrumbJsonLd } from "@/components/Breadcrumbs";
import { ArticleCard } from "@/components/blog/ArticleCard";
import { CATEGORY_BY_SLUG, postsInCategory } from "@/lib/blog-hub";
import type { BlogPost } from "@/lib/blog-types";

const BASE = "https://nsphysiotherapy.co.za";

export const Route = createFileRoute("/blog/category/$slug")({
  loader: ({ params }) => {
    const category = CATEGORY_BY_SLUG[params.slug];
    if (!category) throw notFound();
    return { category, posts: postsInCategory(params.slug) };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return { meta: [{ title: "Category not found" }, { name: "robots", content: "noindex" }] };
    }
    const c = loaderData.category;
    const url = `${BASE}/blog/category/${params.slug}`;
    const title = `${c.name} Physiotherapy Guides | Centurion Physio`;
    const description = `${c.blurb} Physiotherapist written guides and exercises for patients in Centurion and Midrand.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbJsonLd([
              { name: "Home", url: `${BASE}/` },
              { name: "Blog", url: `${BASE}/blog` },
              { name: c.name, url },
            ]),
          ),
        },
      ],
    };
  },
  component: CategoryPage,
  notFoundComponent: () => (
    <PageShell>
      <div className="container-page py-24 text-center">
        <h1 className="font-display text-3xl font-bold text-primary">Category not found</h1>
        <Link to="/blog" className="btn-hero mt-6 inline-flex h-11 items-center rounded-full px-6 font-semibold">
          Back to the hub
        </Link>
      </div>
    </PageShell>
  ),
});

function CategoryPage() {
  const { category, posts } = Route.useLoaderData() as {
    category: { name: string; slug: string; blurb: string };
    posts: BlogPost[];
  };

  return (
    <PageShell>
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Blog", to: "/blog" }, { label: category.name }]} />
      <section className="container-page py-10">
        <div className="max-w-3xl">
          <div className="text-xs font-semibold uppercase tracking-widest text-primary/60">Category</div>
          <h1 className="mt-2 font-display text-3xl font-bold text-primary md:text-4xl">{category.name}</h1>
          <p className="mt-3 text-foreground/80">{category.blurb}</p>
        </div>

        {posts.length === 0 ? (
          <p className="mt-8 text-sm text-muted-foreground">New articles for this area are on the way.</p>
        ) : (
          <ul className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {posts.map((p) => (
              <li key={p.slug}>
                <ArticleCard post={p} />
              </li>
            ))}
          </ul>
        )}

        <div className="mt-10 rounded-2xl border border-border bg-secondary/40 p-6">
          <h2 className="font-display text-lg font-bold text-primary">Want an assessment for {category.name.toLowerCase()}?</h2>
          <p className="mt-2 text-sm text-foreground/75">
            Book in clinic in Eco Park, Centurion, or a home visit across Centurion and Midrand.
          </p>
          <Link to="/booking" className="btn-hero mt-4 inline-flex h-11 items-center rounded-full px-6 font-semibold">
            Book an appointment
          </Link>
        </div>
      </section>
    </PageShell>
  );
}
